import { useEffect, useMemo, useRef, useState } from 'react';
import { Funnel } from '@phosphor-icons/react';
import { useStore } from '../state/store';
import { isDefaultFilters } from '../map/filters';
import { DEFAULT_FILTERS, type TypeFilter } from '../types';

const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'airliner', label: 'Airliners' },
  { value: 'ga', label: 'GA' },
  { value: 'heli', label: 'Helis' },
  { value: 'military', label: 'Military' },
];

function parseAlt(v: string): number | null {
  const n = Number(v);
  return v.trim() === '' || !Number.isFinite(n) ? null : n;
}

export default function FilterPanel() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const filters = useStore((s) => s.filters);
  const setFilters = useStore((s) => s.setFilters);
  const aircraft = useStore((s) => s.aircraft);
  const active = !isDefaultFilters(filters);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const modelOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ac of aircraft.values()) {
      if (ac.typeCode) counts.set(ac.typeCode, (counts.get(ac.typeCode) ?? 0) + 1);
    }
    // Keep the actively-selected model in the list even when none are currently
    // present, so the dropdown reflects the live filter instead of silently
    // snapping back to "All models" while the map stays filtered (empty).
    if (filters.model && !counts.has(filters.model)) counts.set(filters.model, 0);
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [aircraft, filters.model]);

  return (
    <div className={`filter-panel${open ? ' open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className={`tool filter-trigger${active ? ' is-set' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        title="Filter which aircraft are drawn"
      >
        <Funnel size={16} weight={active ? 'fill' : 'bold'} aria-hidden="true" />
        <span className="tool-label">Filters</span>
        {active && <span className="tool-dot" aria-label="(active)" />}
      </button>
      {open && (
        <div className="header-popover filter-body glass">
          <div className="popover-head">
            <span className="popover-title">Filters</span>
            {active && (
              <button type="button" className="text-btn" onClick={() => setFilters(DEFAULT_FILTERS)}>
                Reset
              </button>
            )}
          </div>

          <div className="field">
            <span className="field-label" id="filter-type-label">
              Aircraft type
            </span>
            <div className="segmented" role="radiogroup" aria-labelledby="filter-type-label">
              {TYPE_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={filters.types === o.value}
                  className={filters.types === o.value ? 'active' : ''}
                  onClick={() => setFilters({ types: o.value })}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <label className="field">
            <span className="field-label">Model</span>
            <select
              className="input"
              value={filters.model ?? ''}
              onChange={(e) => setFilters({ model: e.target.value || null })}
            >
              <option value="">All models</option>
              {modelOptions.map(([code, count]) => (
                <option key={code} value={code}>
                  {code} ({count})
                </option>
              ))}
            </select>
          </label>

          <div className="field">
            <span className="field-label">Altitude band (ft)</span>
            <div className="field-pair">
              <input
                className="input mono"
                type="number"
                step={1000}
                min={0}
                value={filters.minAltFt ?? ''}
                placeholder="Min"
                aria-label="Minimum altitude in feet"
                onChange={(e) => setFilters({ minAltFt: parseAlt(e.target.value) })}
              />
              <span className="field-pair-sep" aria-hidden="true">
                to
              </span>
              <input
                className="input mono"
                type="number"
                step={1000}
                min={0}
                value={filters.maxAltFt ?? ''}
                placeholder="Max"
                aria-label="Maximum altitude in feet"
                onChange={(e) => setFilters({ maxAltFt: parseAlt(e.target.value) })}
              />
            </div>
            {filters.minAltFt != null && filters.maxAltFt != null && filters.minAltFt > filters.maxAltFt && (
              <span className="field-error">Minimum is above maximum, so nothing will show.</span>
            )}
          </div>

          <div className="switch-list">
            <label className="switch-row">
              <span>Aircraft on the ground</span>
              <input
                type="checkbox"
                className="switch"
                checked={filters.showGround}
                onChange={(e) => setFilters({ showGround: e.target.checked })}
              />
            </label>
            <label className="switch-row" title="Aircraft at the gate with their transponder off stay where they parked for up to 12 hours">
              <span>Parked aircraft</span>
              <input
                type="checkbox"
                className="switch"
                checked={filters.showGround && filters.showParked !== false}
                disabled={!filters.showGround}
                onChange={(e) => setFilters({ showParked: e.target.checked })}
              />
            </label>
            <label className="switch-row">
              <span>Emergency squawks only</span>
              <input
                type="checkbox"
                className="switch"
                checked={filters.emergencyOnly}
                onChange={(e) => setFilters({ emergencyOnly: e.target.checked })}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
